import { useMemo, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import { GET, POST } from "./api/BackendApi";
import { DistinctTagreadDto, TagreadModel, TagreaderModel } from "./api/Dtos";
import { useGlobalContext } from "./Layout/GlobalState";
import { dateTimeInMsToString } from "./Services/ResultService";

const READER_NAME = "JR";
const MAC_ADDRESS = "JR_MAC_ADDRESS";

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function convertDiffToDelayInMs(diff: number, firstSeenTimestamp: number): number {
  if (firstSeenTimestamp > 100000000000000) {
    return diff / 1000;
  }
  return diff;
}

export default function TagreadsResend() {
  const globalContext = useGlobalContext();
  const [selectedTagreads, setSelectedTagreads] = useState<Array<DistinctTagreadDto>>([]);
  const [selectedStartgroup, setSelectedStartgroup] = useState("");
  const [speedMultiplier, setSpeedMultiplier] = useState<number>(2);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [statusText, setStatusText] = useState("");

  const availableStartgroups = useMemo(() => {
    return [...(globalContext.competition?.startgroups ?? [])].sort(
      (a, b) => a.starttime_planned - b.starttime_planned
    );
  }, [globalContext.competition?.startgroups]);

  const fetchDistinctTagreads = async () => {
    if (selectedStartgroup.trim().length === 0) {
      setStatusText("Ange startgrupp först.");
      return;
    }

    setLoading(true);
    setStatusText("");
    try {
      const response = await GET.distinctTagreadsByStartgroup(selectedStartgroup.trim());
      const sorted = [...response].sort(
        (a, b) => a.firstSeenTimestamp - b.firstSeenTimestamp
      );
      setSelectedTagreads(sorted);
      setStatusText(
        `Laddade ${sorted.length} distinkta tagreads för startgrupp ${selectedStartgroup.trim()}.`
      );
    } catch (error) {
      console.error("Failed to fetch distinct tagreads", error);
      setStatusText("Kunde inte ladda distinkta tagreads.");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (selectedTagreads.length === 0 || resending) {
      return;
    }

    setResending(true);
    setSentCount(0);
    setStatusText(
      `Startar utskick av ${selectedTagreads.length} tagreads (${speedMultiplier}x).`
    );

    try {
      let first: DistinctTagreadDto = selectedTagreads[0];

      for (let i = 0; i < selectedTagreads.length; i++) {
        const current = selectedTagreads[i];
        const diff = Math.max(0, current.firstSeenTimestamp - first.firstSeenTimestamp);
        const baseDelayMs = convertDiffToDelayInMs(diff, first.firstSeenTimestamp);
        const acceleratedDelayMs = Math.floor(baseDelayMs / speedMultiplier);

        if (acceleratedDelayMs > 0) {
          await sleep(acceleratedDelayMs);
        }

        const tagread: TagreadModel = {
          antennaPort: current.antennaPort,
          epc: current.epc,
          firstSeenTimestamp: current.firstSeenTimestamp,
          isHeartBeat: false,
        };

        const payload: TagreaderModel = {
          reader_name: READER_NAME,
          mac_address: MAC_ADDRESS,
          tag_reads: [tagread],
        };

        await POST.tagread(payload);
        setSentCount(i + 1);
        first = current; // Säkerställ att "first" alltid är satt, även om det skulle vara en edge case med tom array (som inte borde hända)
      }

      setStatusText(
        `Klart. Skickade ${selectedTagreads.length} tagreads för startgrupp ${selectedStartgroup}.`
      );
    } catch (error) {
      console.error("Failed to resend tagreads", error);
      setStatusText("Ett fel uppstod under utskick av tagreads.");
    } finally {
      setResending(false);
    }
  };

  return (
    <VStack align="start" spacing={4}>
      <Text fontSize="24"><b>TEST</b> - Skicka om tagreads - <b>TEST</b></Text>
      <Text>
        <b>Används bara för TEST!</b><br />
        Ta en backup av databasen och radera resultat för den aktuella startgruppen innan du fortsätter.<br />
        Hämtar distinkta tagreads från /api/tagreads/distinctstartgroup=... och
        skickar dem till /api/tagread i tidsordning.<br />
        Tävlingen måste vara "öppen" och startgruppen måste "aktiv".<br />
      </Text>

      <HStack spacing={4} align="end" flexWrap="wrap">
        <FormControl width="260px">
          <FormLabel>Startgrupp</FormLabel>
          <Select
            placeholder="Välj startgrupp"
            value={selectedStartgroup}
            onChange={(e) => setSelectedStartgroup(e.currentTarget.value)}
            isDisabled={loading || resending}
          >
            {availableStartgroups.map((startgroup) => (
              <option key={startgroup.id} value={String(startgroup.id)}>
                #{startgroup.id} - {dateTimeInMsToString(startgroup.starttime_planned)}
              </option>
            ))}
          </Select>
        </FormControl>

        <Button
          colorScheme="blue"
          onClick={fetchDistinctTagreads}
          isLoading={loading}
          isDisabled={resending}
        >
          Ladda distinkta tagreads
        </Button>

        <FormControl width="180px">
          <FormLabel>Hastighet</FormLabel>
          <Select
            value={String(speedMultiplier)}
            onChange={(e) => setSpeedMultiplier(Number(e.currentTarget.value))}
            isDisabled={resending}
          >
            <option value="2">2x snabbare</option>
            <option value="5">5x snabbare</option>
            <option value="10">10x snabbare</option>
          </Select>
        </FormControl>

        <Button
          colorScheme="green"
          onClick={resend}
          isDisabled={selectedTagreads.length === 0 || resending || loading}
          isLoading={resending}
        >
          Skicka om
        </Button>
      </HStack>

      <Text>
        Vald startgrupp innehåller {selectedTagreads.length} tagreads.
        {resending ? ` Skickat ${sentCount}/${selectedTagreads.length}.` : ""}
      </Text>

      {statusText.length > 0 && <Text>{statusText}</Text>}
    </VStack>
  );
}
